import { Product, Package } from "../types/product.types";
import { FilterDataProps } from "../types/filter.types";
import { cartTags } from "../types/tag";
import { CartData, CartItem } from "../types/cart.types";
import { getTags } from "../constants/tags";
import { StorageItem } from "../types/storageItem.types";
import { isISODate, normalizeDate, slashDMYtoISO } from "./calendar";
interface FormData {
    [key: string]: any;
}

const getPriceRange = (filterData: FilterDataProps) => [
    filterData.cena?.[0] ?? 0,
    filterData.cena?.[1] ?? 100000,
] as const;

const hasSelectedOptions = (values?: Record<string, boolean>) =>
    Boolean(values && Object.values(values).some(Boolean));

const isStorageOnlyFilterActive = (filterData: FilterDataProps) =>
    Boolean(filterData.typy_jidla?.storage);

const hasActiveNonPriceFilters = (filterData: FilterDataProps) =>
    Object.entries(filterData).some(([tagCategory, tagValues]) => {
        if (tagCategory === "cena") return false;
        return hasSelectedOptions(tagValues as Record<string, boolean> | undefined);
    });

const normalizeStorageBoundaryDate = (value?: string | null): string | null => {
    if (typeof value !== "string" || value.trim() === "") {
        return null;
    }

    const trimmedValue = value.trim();
    const normalized = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmedValue)
        ? slashDMYtoISO(trimmedValue)
        : normalizeDate(trimmedValue);
    return isISODate(normalized) ? normalized : null;
};

const isStorageVisibleForDate = (
    item: StorageItem,
    selectedDate?: string,
) => {
    if (item.show_in_eshop === false) {
        return false;
    }

    if (typeof selectedDate !== "string" || selectedDate.trim() === "") {
        return true;
    }

    const normalizedSelectedDate = normalizeDate(selectedDate.trim());
    if (!isISODate(normalizedSelectedDate)) {
        return true;
    }

    const visibleFrom = normalizeStorageBoundaryDate(item.order_from);
    const visibleTo = normalizeStorageBoundaryDate(item.order_to);

    if (visibleFrom && normalizedSelectedDate < visibleFrom) {
        return false;
    }

    if (visibleTo && normalizedSelectedDate > visibleTo) {
        return false;
    }

    return true;
};

/**
* Filters and returns recommended products based on form data filters.
* Ensures a minimum number of products by filling with fallback products if needed.
* 
* @param allProducts - List of all available products.
* @param formData - Object containing filters with keys like 'actionType', 'specialRequests'.
* @param minProducts - Minimum number of products to return (default 4).
* 
* @returns Array of recommended products matching the filters.
*/
export const filterRecommendedProducts = (
    allProducts: (Product| Package)[],
    formData: FormData,
    minProducts: number = 4
): (Product | Package)[] => {
    const targetPeople = Number((formData as any)?.peopleAmount ?? NaN);
    // People distance helper: 0 if target inside [min,max], else distance to nearest bound.
    const peopleDistance = (p: Product | Package, target: number): number => {
        const min = p.min_participants != null ? Number(p.min_participants) : null;
        const max = p.max_participants != null ? Number(p.max_participants) : null;

        if (!Number.isFinite(target) || (min == null && max == null)) {
            return Number.POSITIVE_INFINITY;
        }

        if (min != null && max != null) {
            if (target >= min && target <= max) return 0;
            // distance to nearest bound if outside range
            return Math.min(Math.abs(target - min), Math.abs(target - max));
        }

        if (min != null) return Math.abs(target - min);
        if (max != null) return Math.abs(target - max);

        return Number.POSITIVE_INFINITY;
    };

    // People range helpers
    const rangeCenter = (p: Product | Package): number | null => {
        const min = p.min_participants != null ? Number(p.min_participants) : null;
        const max = p.max_participants != null ? Number(p.max_participants) : null;
        if (min != null && max != null) return (min + max) / 2;
        if (min != null) return min; 
        if (max != null) return max;       
        return null;
    };
    const rangeWidth = (p: Product | Package): number => {
        const min = p.min_participants != null ? Number(p.min_participants) : null;
        const max = p.max_participants != null ? Number(p.max_participants) : null;
        if (min != null && max != null) return Math.abs(max - min);
        return Number.POSITIVE_INFINITY;
    };


    // Create list of filtered products
    const filteredProducts = allProducts.filter((product: Product | Package) => {
        // Handle products without tags
        if (!product.tags)  return false;

        const matchesAllFilters = Object.entries(formData).every(([tagCategory, tagValue]) => {
            // Ignore other onboarding categories
            if (tagCategory !== "actionType" && tagCategory !== "specialRequests" ) {
                return true
            }
            /*
            if (tagCategory === "peopleAmount") {
                const minPeople = product.min_participants;
                const maxPeople = product.max_participants;
                if (!maxPeople || !minPeople || !tagValue) return true;
                if (Number(minPeople) > Number(tagValue) || Number(maxPeople) < Number(tagValue)) {
                    return false;
                }
                return true;
            }\*/

            // Handle action type
            if (tagCategory === "actionType"){
                const tagGroup = product.tags.prilezitosti;
                // Return false if it doesnt exist
                if (!tagGroup) return false;
                // Filter out empty values
                const selectedTags = (tagValue as string[]).filter(tag => tag !== '');
                if (selectedTags.length === 0) return true;
                // Return true if atleast 1 value matches
                return selectedTags.some(entry => tagGroup[entry as keyof typeof tagGroup] === true);
            }
            // Handle action type
            if (tagCategory === "specialRequests"){
                const tagGroup = product.tags.diety;
                // Return false if it doesnt exist
                if (!tagGroup) return false;
                // Filter out empty values
                const selectedTags = (tagValue as string[]).filter(tag => tag !== '');
                if (selectedTags.length === 0) return true;
                // Return true if atleast 1 value matches
                // Ted vraci jen pokud ma vsechny values
                if( selectedTags.every(entry => tagGroup[entry as keyof typeof tagGroup] === true)){
                    
                    /*
                    log(product)
                    log(selectedTags);
                    log(tagGroup)
                    */
                }
                return selectedTags.every(entry => tagGroup[entry as keyof typeof tagGroup] === true);
            }
            // Handle any other cases
            return true;
        })
        return matchesAllFilters;
    })


    // Sort by closeness to targetPeople
    const sortedFiltered = [...filteredProducts].sort((a, b) => {
        const da = peopleDistance(a, targetPeople);
        const db = peopleDistance(b, targetPeople);
        if (da !== db) return da - db;

        // If both are equally close, prefer the one whose center is closer to target
        const ca = rangeCenter(a);
        const cb = rangeCenter(b);
        if (ca != null && cb != null) {
            const dca = Math.abs(targetPeople - ca);
            const dcb = Math.abs(targetPeople - cb);
            if (dca !== dcb) return dca - dcb;
        }

        // Then prefer narrower ranges
        const wa = rangeWidth(a);
        const wb = rangeWidth(b);
        if (wa !== wb) return wa - wb;

        // Final tie-breaker: id
        return String(a.id).localeCompare(String(b.id));
    });

    // Tady jsem zmenil abych ukazoval jen ty co sedi
    // If we got at least 4 filtered products, use them
    if (sortedFiltered.length >= minProducts) {
        return sortedFiltered;
    }else{
        return sortedFiltered;
    }
    // Fallback products in there arent enought filtered products
    const fallbackProducts = [...filteredProducts];
    const usedIds = filteredProducts.map(p=> p.id)
    // Go through all the products only if there are less that the minimum
    for( const product of allProducts){
        if(fallbackProducts.length >= minProducts ) break;
        // Add to fallback products if they dont already include it
        if(!usedIds.includes(product.id)){
            fallbackProducts.push(product)
            usedIds.push(product.id)
        }
    }
    return fallbackProducts;
}

/**
* Filters products based on the given filter data.
* Supports filtering by price range and tag selections.
* 
* @param allProducts - List of all products.
* @param filterData - Filter criteria grouped by categories.
* 
* @returns Array of products that match all filter criteria.
*/
export const getFilteredProducts = (
    allProducts: (Product | Package)[],
    filterData: FilterDataProps,
) => {
    const [filteredMinPrice, filteredMaxPrice] = getPriceRange(filterData);
    const priceFilterActive = filteredMinPrice !== 0 || filteredMaxPrice !== 100000;

    // Create list of filtered products
    const filteredProducts = allProducts.filter((product: Product | Package) => {
        // Products without tags only pass while no tag filter is active
        if (!product.tags) return !hasActiveNonPriceFilters(filterData) && !priceFilterActive;
        const matchesAllFilters = Object.entries(filterData).every(([tagCategory, tagValues]) => {
            // Skip missing filter groups
            if (!tagValues) {
                return true;
            }
            // Separate price filter logic
            if ( tagCategory === 'cena') {
                const minPrice = product.pricing?.price_without_tax
                if(minPrice == null){
                    // Products without a price only pass while the price filter is untouched
                    return !priceFilterActive;
                }
                // Check if products price is in filtered margin
                if (minPrice >= filteredMinPrice && minPrice <= filteredMaxPrice) {
                    return true;
                }else{
                    return false;
                }
            }
            // Extract the selected tags
            const selectedTags = Object.entries(tagValues).filter(([tagKey, isSelected])=> isSelected === true)
            // Return true if no tags are selected
            if (selectedTags.length === 0) return true;
            // Get the product tag group
            const tagGroup = product.tags[tagCategory as keyof typeof product.tags]
            // A product missing the tag group can't match an active filter
            if (!tagGroup) return false;
            if (tagCategory === "typy_jidla" && selectedTags.some(([tagKey]) => tagKey === "storage")) {
                return false;
            }
            // See if atleast one matches
            return selectedTags.some(([tagKey]) => tagGroup[tagKey as keyof typeof tagGroup] === true);
        })
        return matchesAllFilters;
    })
    return filteredProducts;
}

export const getFilteredStorageItems = (
    items: StorageItem[],
    filterData: FilterDataProps,
    selectedDate?: string,
) => {
    const [filteredMinPrice, filteredMaxPrice] = getPriceRange(filterData);
    const storageOnly = isStorageOnlyFilterActive(filterData);
    const priceFilterActive = filteredMinPrice !== 0 || filteredMaxPrice !== 100000;
    const visibleItems = items.filter((item) => isStorageVisibleForDate(item, selectedDate));

    if (storageOnly) {
        return visibleItems;
    }

    if (!storageOnly && hasActiveNonPriceFilters(filterData)) {
        return [];
    }

    return visibleItems.filter((item) => {
        const minPrice = item.pricing?.price_without_tax;
        if (minPrice == null) {
            return !priceFilterActive;
        }

        return minPrice >= filteredMinPrice && minPrice <= filteredMaxPrice;
    });
}

/**
* Extracts tags from a product that match the given minimal cart tags.
* 
* @param product - Product object with tags.
* @param minCart - Minimal cart tags to match.
* @returns Object of tag categories mapped to matched tag keys.
*/
function getSelectedTags(product: Product | Package, minCart: cartTags[]) {
    const result: Record<string, string[]> = {};
    if (!product.tags) return result; 
    // Loop through all the categories
    for (const [tagCat, tagVals] of Object.entries(product.tags)) {
        const cartCategory = minCart.find(cartItem => cartItem.category === tagCat); // Find matching cart category
        if (!cartCategory) continue;
        // Loop through the tags
        const selectedTags = Object.entries(tagVals)
        .filter(([tagName, tagVal]) => tagVal === true && cartCategory.tags.includes(tagName)) // Only return the tags that match
        .map(([tagName]) => tagName);
        if (selectedTags.length > 0) {
            result[tagCat] = selectedTags;
        }
    }
    return result;
}

/**
* Extracts tags from products currently in the cart.
* 
* @param products - All products list.
* @param cartData - Current cart items.
* @returns Array of cartTags (category and tags) extracted from cart products.
*/
function getCartTags(products: (Product | Package)[], cartData: CartData) {
    const allCartItems: CartItem[] = Object.values(cartData.items).flat(); // Flatten all CartItem[] from all dates

    const res = allCartItems.map(({ id }) => {
        const product = products.find((product) => product.id === id);
        if (!product || !product.tags) return null;

        const result: { category: string; tags: string[] }[] = [];
        
        for (const [tagCat, tagVals] of Object.entries(product.tags)) {
            const selectedTags = Object.entries(tagVals)
                .filter(([_, tagVal]) => tagVal === true)
                .map(([tagName]) => tagName);

            if (selectedTags.length > 0) {
                result.push({ category: tagCat, tags: selectedTags });
            }
        }

        return result;
    });

    return res;
}


/**
* Recommends products based on how well their tags match the tags of products in the cart.
* Returns the top 25 matches sorted by the number of matching tags.
* 
* @param products - All available products.
* @param cartData - Current cart items.
* @returns Array of recommended product IDs and their matched tags.
*/
export const recommendProducts = (products: (Product | Package)[], cartData: CartData) => {
    const cartTags = getCartTags(products, cartData).flat().filter((x): x is cartTags => !!x)
    const recommendedProducts = products
    .map(p => {
        const tags = getSelectedTags(p, cartTags); // Extract mathing tags
        const matchScore = Object.values(tags).reduce((sum, arr) => sum + arr.length, 0); // Claculate mtches
        return { id: p.id, tags, matchScore };
    })
    .filter(p => Object.keys(p.tags).length > 0) // Filter out products without matches
    .sort((a, b) => b.matchScore - a.matchScore) // Sort by most matches
    .slice(0, 25) // Get limited number
    .map(({ id, tags }) => ({ id, tags })); // Remove the matchscore
    return recommendedProducts;
}




type HasIdUpsell = { id: number; type?: string; upsell?: boolean };

const isUpsellSales = (p: any): p is HasIdUpsell =>
    p && typeof p.id === "number" && p.type === "sales" && p.upsell === true;

/**
* Returns only products destined for upsell
* 
* @param products - All available products.
* @returns Array of filtered product`s ids
*/
export const filterUpsell = (products: (Product | Package)[]): number[] => {
    return products
    .filter(isUpsellSales)
    .slice(0, 25)
    .map(p => p.id);
};

// Format selected filters into simple data
export const formatSelectedFilters = (
    filterData: FilterDataProps,
    t: (key: string) => string
) => {
    const tags = getTags(t);
    const filters = Object.entries(filterData).flatMap(([tagCategory, tagValues]) =>
        tagValues
            ? Object.entries(tagValues)
                .filter(([tagKey, isSelected]) => isSelected && tagCategory !== "cena")
                .map(([tagKey]) => ({
                    tagCategory,
                    tagSlug: tagKey,
                    tag:
                        tags.find((tag) => tag.slug === tagCategory)?.tags.find((tagItem: any) => tagItem.slug === tagKey)
                            ?.text ?? tagKey,
                }))
            : []
    );
    return filters;
};

// Check if two values are deeply equal ( used for complex shapes ex. filterData)
export function deeplyEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
}

// Selected category
type SelectedByCat = Partial<Record<keyof FilterDataProps, string[]>>;
// Set filters from the onboarding
export function applySelected(
    selected: SelectedByCat,
    filterData: FilterDataProps,
    setAllFilters: (next: FilterDataProps) => void,
    tagsDef: ReturnType<typeof getTags>
) {
    // start from current to preserve fields like "cena"
    const next: FilterDataProps = { ...filterData };

    // Reset all to false
    for (const cat of tagsDef) {
        if (cat.slug === "cena") continue;
        // Set tag to false
        const blank = Object.fromEntries(cat.tags.map(t => [t.slug, false])) as Record<string, boolean>;
        // Updatae it in the tags
        (next as any)[cat.slug] = blank;
    }

    // Apply selected filers
    for (const [key, slugs] of Object.entries(selected)) {
        if (key === "cena" || !slugs?.length) continue;
        // Find the category
        const cat = tagsDef.find(c => c.slug === key);
        if (!cat) continue;

        const map = (next as any)[key] as Record<string, boolean>;
        for (const s of slugs) {
            if (s in map) {
                map[s] = true;
            }
        }
    }
    // Update once
    setAllFilters(next);
}
